import { format } from 'date-fns';
import { Api } from '../api';
import { applications } from '../endpoints';
import type {
  ApplicationEntity,
  ApplicationItem,
  ApplicationPdfRequest,
  ApplicationSearchParams,
  AssignApplicationRequest,
  AssignManualApplicationRequest,
  ManualApplicationDownloadParams,
  ManualApplicationEntity,
  ManualApplicationFileSearchParams,
  ManualApplicationSearchParams,
  SupplementaryApplicationEntity,
} from './models';

const getDatePart = (date: Date | undefined) => {
  if (!date) return undefined;

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return [year, pad2(month), pad2(day)].join('-');
};

const pad2 = (v: number): string => {
  if (v < 10) return '0' + v.toString();
  return v.toString();
};

export class ApplicationApi extends Api {
  static getApplications = (searchParams: ApplicationSearchParams = {}, signal?: AbortSignal) => {
    const params = {
      ApplicationName: searchParams.ApplicationName,
      Passport: searchParams.Passport,
      ModifiedOn: getDatePart(searchParams.ModifiedOn),
      Status: searchParams.status,
    };

    return this.get<ApplicationItem[]>(applications.getApplications(), { queryParams: params, signal });
  };

  static getApplicationsByCustomer = (customerId: string, signal?: AbortSignal) =>
    this.get<ApplicationItem[]>(applications.getApplication(), { queryParams: { customerId }, signal });

  static getSupplementaryApplication = (applicationId: string, signal?: AbortSignal) =>
    this.get<SupplementaryApplicationEntity>(applications.getSupplementApplication(applicationId), { signal });

  static getMainApplication = (applicationId: string, signal?: AbortSignal) =>
    this.get<ApplicationEntity>(applications.getMainApplication(applicationId), { signal });

  static searchManualApplications = (searchParams: ManualApplicationSearchParams = {}, signal?: AbortSignal) =>
    this.get<ManualApplicationEntity[]>(applications.getManualApplication(), { queryParams: searchParams, signal });

  static getPdf = (queryParams: ApplicationPdfRequest, signal?: AbortSignal) =>
    this.get<Blob>(applications.pdf(), { queryParams, headers: { Accept: 'application/pdf' }, responseType: 'blob', signal });

  static saveSupplement = (applicationData: SupplementaryApplicationEntity, signal?: AbortSignal) => {
    const data = { ...applicationData };
    //@ts-expect-error types
    delete data.insertedDate;
    if (typeof data.securityAnswer === 'number') data.securityAnswer = (data.securityAnswer as number).toString();
    // @ts-expect-error types
    if (data.anticipatedMonthlySpend === '') delete data.anticipatedMonthlySpend;

    return this.post<string>(applications.saveSupplement(), data, { signal });
  };

  static saveApplication = (applicationData: ApplicationEntity, signal?: AbortSignal) => {
    const data = { ...applicationData };
    // @ts-expect-error types
    delete data.processedDate;
    // @ts-expect-error types
    delete data.insertedDate;
    if (typeof data.passportExpiryDate === 'object' && (data.passportExpiryDate as object) instanceof Date)
      data.passportExpiryDate = format(data.passportExpiryDate, 'yyyy-MM-dd');
    if (typeof data.dob === 'object' && (data.dob as object) instanceof Date) data.dob = format(data.dob, 'yyyy-MM-dd');
    if (typeof data.securityAnswer === 'number') data.securityAnswer = (data.securityAnswer as number).toString();
    // @ts-expect-error types
    if (data.anticipatedMonthlySpend === '') delete data.anticipatedMonthlySpend;

    return this.post<string>(applications.saveApplication(), data, { signal });
  };

  static approveCreditLimit = (applicationId: string, signal?: AbortSignal) =>
    this.post(applications.approveCreditLimit(applicationId), { responseType: 'none', signal });

  static rejectCreditLimit = (applicationId: string, signal?: AbortSignal) =>
    this.post(applications.rejectCreditLimit(applicationId), { responseType: 'none', signal });

  static submitApplication = (applicationId: string, signal?: AbortSignal) =>
    this.post(applications.submitApplication(applicationId), { responseType: 'none', signal });

  static rejectApplication = (applicationId: string, signal?: AbortSignal) =>
    this.post(applications.rejectApplication(applicationId), { responseType: 'none', signal });

  static assignApplication = (request: AssignApplicationRequest, signal?: AbortSignal) =>
    this.post(applications.assignApplication(request.applicationId), request.data, { responseType: 'none', signal });

  static assignManualApplication = (data: AssignManualApplicationRequest, signal?: AbortSignal) =>
    this.post(applications.assignManualApplication(), data, { responseType: 'none', signal });

  static rejectManualApplicationFile = (searchParams: ManualApplicationFileSearchParams, signal?: AbortSignal) =>
    this.post(
      applications.rejectManualApplicationFile(searchParams.fileId),
      { comment: searchParams.comment || '' },
      { responseType: 'none', signal },
    );

  static submitManualApplication = (manualApplicationId: string, signal?: AbortSignal) =>
    this.post(applications.submitManualApplication(), { manualApplicationId }, { responseType: 'none', signal });

  static approveManualApplication = (searchParams: ManualApplicationDownloadParams, signal?: AbortSignal) =>
    this.post(applications.completeManualApplication(), searchParams, { responseType: 'none', signal });

  static rejectManualApplication = (searchParams: ManualApplicationDownloadParams, signal?: AbortSignal) =>
    this.post(applications.rejectManualApplication(), searchParams, { responseType: 'none', signal });
}
